import React, { useEffect, useReducer } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import uuid from 'uuid';
import numeral from 'numeral';

import makeStyles from '@mui/styles/makeStyles';
import TextField from '@mui/material/TextField';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';

import { AndOrButton, OrCard, OrCardBody, OrCardHeader } from 'widgets-v5/and-or-list';
import TextFieldFormatter from 'widgets-v6/text-field-formatter';
import { PLATFORM_MAPPING } from 'common/constants/platforms';
import { SimpleSelect, GroupedSingleSelect } from 'widgets-v6/select';
import { CustomContentSelect } from 'widgets-v6/select';
import Spacer from 'widgets-v5/spacer';
import schema from './creative-rotation-by-weather/schema';
import { getEnvironmentSettings } from 'services/environment';
import {
    getInputAdornmentMap,
    getIsWeatherTypeTemperature,
    getValidWeatherConditionMultipleOptionType,
} from 'states/resources/weather-conditions/selectors';
import {
    useWeatherConditions,
    getWeatherConditionQualifier,
    getWeatherConditionTimeframe,
    getWeatherConditionValue,
} from '../weather-conditions/hook';

const useStyles = makeStyles(() => ({
    creativeInfoSelector: {
        whiteSpace: 'normal',
        overflow: 'auto',
    },
    previewImg: {
        width: '100%',
    },
    previewImgSelector: {
        width: 'auto',
        maxHeight: 100,
        maxWidth: 200,
        objectFit: 'contain',
    },
    previewImgColumn: {
        marginRight: 24,
        display: 'flex',
        alignItems: 'center',
    },
    disabledSelectOption: {
        backgroundColor: '#f2f2f2',
        color: '#999999',
        cursor: 'not-allowed',
    },
    footerText: {
        textAlign: 'center',
        padding: '16px 24px',
    },
    creativeField: {
        marginBottom: 8,
    },
    // Reserves height when markupId is empty so selecting a creative does not shift the rule row
    creativeSelectHeightSpacer: {
        minHeight: 72,
    },
}));

const handlers = {
    CREATIVE_ROTATION_BY_WEATHER__ADD_A_NEW_SETTING: state => {
        return {
            ...state,
            draft: [
                ...state.draft,
                {
                    id: uuid.v4(),
                    qualifier: '',
                    type: '',
                    timeframe: '',
                    amount: '',
                    markupId: null,
                    isDefault: false,
                },
            ],
        };
    },
    CREATIVE_ROTATION_BY_WEATHER__REMOVE_A_SETTING: (state, action) => {
        return {
            ...state,
            draft: _.filter(state.draft, setting => setting.id !== action.payload.id),
        };
    },
    CREATIVE_ROTATION_BY_WEATHER__UPDATE_A_SETTING: (state, action, isWeatherTempType) => {
        const { id, field, value } = action.payload;

        return {
            ...state,
            draft: _.map(state.draft, setting => {
                if (setting.id === id) {
                    const updatedSetting = {
                        ...setting,
                        [field]: value,
                    };

                    if (field === 'type') {
                        updatedSetting.amount = '';
                        updatedSetting.timeframe = '';
                        // Categorical conditions use a fixed "equal" qualifier; numeric types pick qualifier in the row UI
                        updatedSetting.qualifier = !isWeatherTempType(value) ? 'equal' : '';
                        updatedSetting.amount = '';
                    }

                    return updatedSetting;
                }
                return setting;
            }),
        };
    },
};

const validateDraft = state => {
    const options = {
        allowUnknown: true,
        abortEarly: false,
    };

    const result = schema.validate(state.draft, options);

    if (!result.error) {
        return {
            ...state,
            errors: {},
        };
    }

    const errors = {};

    _.each(result.error.details, detail => {
        errors[`rotation_rules.weather.${detail.path.join('.')}`] = detail.message;
    });

    return {
        ...state,
        errors,
    };
};

/**
 * Builds the weather rotation draft reducer with API-backed temperature-type detection.
 *
 * @param {(type: string) => boolean} isWeatherTempType - From Redux weather constants (see weather-conditions/index.js)
 * @returns {Function} Reducer for local draft state and validation errors
 */
const createReducer = isWeatherTempType => (state, action) => {
    let nextState = state;

    if (handlers[action.type]) {
        if (action.type === 'CREATIVE_ROTATION_BY_WEATHER__UPDATE_A_SETTING') {
            nextState = handlers[action.type](state, action, isWeatherTempType);
        } else {
            nextState = handlers[action.type](state, action);
        }
        nextState = validateDraft(nextState);
        return nextState;
    }

    return state;
};

/**
 * Weather-based creative rotation rules for the ad form.
 *
 * @param {Object} props
 * @param {Object} props.errors - Form validation errors keyed by field path
 * @param {boolean} props.showErrors - Whether to surface validation errors in inputs
 * @param {Array<Object>} props.creatives - Campaign creatives available for rule assignment
 * @param {Object} props.campaign - Campaign record; used to pass campaignId into weather condition options
 * @param {Array<Object>} props.weatherRotationSettings - Initial weather rotation rule draft
 * @param {Function} props.onChange - Called when draft or validation errors change
 * @param {Object} props.rotationRules - Existing rotation_rules payload merged on change
 * @param {boolean} props.isCrossPlatformCampaign - Shows platform chips on creative options
 * @param {boolean} props.useAspectRatio - Display aspect ratio instead of size on creatives
 */
export const CreativeRotationByWeatherTable = ({
    errors,
    showErrors,
    creatives,
    campaign,
    weatherRotationSettings,
    onChange,
    rotationRules,
    isCrossPlatformCampaign,
    useAspectRatio,
}) => {
    const classes = useStyles();
    // Parent passes campaign from creative-rotation-selector; hook uses id for weather options API alignment
    const campaignId = campaign?.id;

    // Weather constants now live in Redux (see weather-conditions/index.js); previously imported from deleted weather-condition-targeting.js
    const weatherData = {
        validWeatherConditionMultipleOptionType: useSelector(
            getValidWeatherConditionMultipleOptionType
        ),
        isWeatherTypeTemperature: useSelector(getIsWeatherTypeTemperature),
        inputAdornmentMap: useSelector(getInputAdornmentMap),
    };

    const initialState = {
        draft: weatherRotationSettings,
        errors: {},
    };

    // Same createReducer pattern as weather-conditions/index.js — keeps type-change logic in sync with Redux constants
    const reducer = createReducer(weatherData.isWeatherTypeTemperature);
    const [state, dispatch] = useReducer(reducer, initialState);

    useEffect(() => {
        const nonCreativeRotationByWeatherErrors = _.omitBy(errors, (value, key) =>
            key.startsWith('rotation_rules.weather')
        );
        onChange({
            errors: {
                ...nonCreativeRotationByWeatherErrors,
                ...state.errors,
            },
            draft: {
                rotation_rules: {
                    ...rotationRules,
                    weather: state.draft,
                },
            },
        });
    }, [state]);

    const handleAddSettingClick = () => {
        dispatch({
            type: 'CREATIVE_ROTATION_BY_WEATHER__ADD_A_NEW_SETTING',
        });
    };

    const handleSettingChange = id => ({ field, value }) => {
        dispatch({
            type: 'CREATIVE_ROTATION_BY_WEATHER__UPDATE_A_SETTING',
            payload: {
                id,
                field,
                value,
            },
        });
    };

    const handleDeleteSetting = id => {
        dispatch({
            type: 'CREATIVE_ROTATION_BY_WEATHER__REMOVE_A_SETTING',
            payload: {
                id,
            },
        });
    };

    const nonDefaultSettings = state.draft.filter(setting => !setting.isDefault);
    const defaultSettings = state.draft.filter(setting => setting.isDefault);

    return (
        <React.Fragment>
            <Spacer type="medium" />
            {nonDefaultSettings.map((setting, displayIndex) => (
                <React.Fragment key={setting.id}>
                    <RotationSettingNonDefaultRow
                        setting={setting}
                        creatives={creatives}
                        weatherData={weatherData}
                        campaignId={campaignId}
                        onChange={handleSettingChange(setting.id)}
                        onDelete={handleDeleteSetting}
                        displayIndex={displayIndex + 1}
                        draftIndex={state.draft.findIndex(s => s.id === setting.id)}
                        showErrors={showErrors}
                        errors={errors}
                        isCrossPlatformCampaign={isCrossPlatformCampaign}
                        useAspectRatio={useAspectRatio}
                    />
                    <Spacer type="medium" />
                </React.Fragment>
            ))}
            {defaultSettings.map(setting => (
                <React.Fragment key={setting.id}>
                    <RotationSettingDefaultRow
                        setting={setting}
                        creatives={creatives}
                        onChange={handleSettingChange(setting.id)}
                        draftIndex={state.draft.findIndex(s => s.id === setting.id)}
                        showErrors={showErrors}
                        errors={errors}
                        isCrossPlatformCampaign={isCrossPlatformCampaign}
                        useAspectRatio={useAspectRatio}
                    />
                    <Spacer type="medium" />
                </React.Fragment>
            ))}
            <Typography variant="body1" className={classes.footerText}>
                Rules are matched starting from top to bottom. The default rule is used when no
                other rules are matched.
            </Typography>
            <Spacer type="large" />
            <AndOrButton>
                <Button color="primary" onClick={handleAddSettingClick}>
                    ADD A RULE
                </Button>
            </AndOrButton>
        </React.Fragment>
    );
};

/**
 * @param {Object} props
 * @param {Object} props.setting - Default weather rotation rule state
 * @param {Array<Object>} props.creatives - Campaign creatives for the creative selector
 * @param {Function} props.onChange - Field update handler for this row
 * @param {number} props.draftIndex - Index in rotation_rules.weather array for error keys
 * @param {boolean} props.showErrors - Whether to show validation errors
 * @param {Object} props.errors - Form validation errors
 * @param {boolean} props.isCrossPlatformCampaign - Shows platform chips on creative options
 * @param {boolean} props.useAspectRatio - Display aspect ratio instead of size on creatives
 */
const RotationSettingDefaultRow = ({
    setting,
    creatives,
    onChange,
    draftIndex,
    showErrors,
    errors,
    isCrossPlatformCampaign,
    useAspectRatio,
}) => {
    const classes = useStyles();
    return (
        <OrCard>
            <OrCardHeader showRemove={false}>Default Rule</OrCardHeader>
            <OrCardBody>
                <Box m={2}>
                    <Grid container alignItems="flex-start" spacing={3}>
                        <Grid item xs={12} sm={6} md={3}>
                            <SimpleSelect
                                disabled={true}
                                label="Condition"
                                placeholder="Default Rule"
                                value={setting.type}
                            />
                        </Grid>
                        <Grid item xs={12} className={classes.creativeField}>
                            <CreativeSelect
                                setting={setting}
                                draftIndex={draftIndex}
                                creatives={creatives}
                                onChange={onChange}
                                showErrors={showErrors}
                                errors={errors}
                                isCrossPlatformCampaign={isCrossPlatformCampaign}
                                useAspectRatio={useAspectRatio}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </OrCardBody>
        </OrCard>
    );
};

/**
 * @param {Object} props
 * @param {Object} props.setting - Single weather rotation rule row state
 * @param {Array<Object>} props.creatives - Campaign creatives for the creative selector
 * @param {Object} props.weatherData - Weather constants from Redux (replaces deleted weather-condition-targeting imports)
 * @param {string[]} props.weatherData.validWeatherConditionMultipleOptionType
 * @param {(type: string) => boolean} props.weatherData.isWeatherTypeTemperature
 * @param {Record<string, string>} props.weatherData.inputAdornmentMap
 * @param {number|string} props.campaignId - Campaign id for weather condition options hook
 * @param {Function} props.onChange - Field update handler for this row
 * @param {Function} props.onDelete - Removes this rule row
 * @param {boolean} props.showErrors - Whether to show validation errors
 * @param {Object} props.errors - Form validation errors
 * @param {number} props.displayIndex - 1-based rule number for header label
 * @param {number} props.draftIndex - Index in rotation_rules.weather array for error keys
 * @param {boolean} props.isCrossPlatformCampaign - Shows platform chips on creative options
 * @param {boolean} props.useAspectRatio - Display aspect ratio instead of size on creatives
 */
const RotationSettingNonDefaultRow = ({
    setting,
    creatives,
    weatherData,
    campaignId,
    onChange,
    onDelete,
    showErrors,
    errors,
    displayIndex,
    draftIndex,
    isCrossPlatformCampaign,
    useAspectRatio,
}) => {
    const classes = useStyles();
    const environmentSettings = getEnvironmentSettings();

    // campaignId matches weather-conditions rows; data is pre-loaded on ad-form open but hook can re-fetch if needed
    const { conditionOptions } = useWeatherConditions({
        conditionsToUse: environmentSettings.getWeatherCondtionsToUse(),
        campaignId,
    });

    const hasConditionSelected = Boolean(setting.type);

    return (
        <OrCard>
            <OrCardHeader onRemove={() => onDelete(setting.id)} removeTooltipTitle="Remove rule">
                <div className="ef6-alignment__flex">{`Rule ${displayIndex}`}</div>
            </OrCardHeader>
            <OrCardBody>
                <Box m={2}>
                    <Grid container alignItems="flex-start" spacing={3}>
                        <Grid item xs={12} sm={6} md={3}>
                            <GroupedSingleSelect
                                label="Condition"
                                required={true}
                                value={setting.type}
                                options={conditionOptions}
                                onChange={value => {
                                    onChange({ field: 'type', value });
                                }}
                                placeholder="Select condition"
                                error={
                                    showErrors &&
                                    errors[`rotation_rules.weather.${draftIndex}.type`]
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            {!setting.type ||
                            weatherData.validWeatherConditionMultipleOptionType.includes(
                                setting.type
                            ) ? (
                                <SimpleSelect
                                    label="Qualifier"
                                    required={hasConditionSelected}
                                    value={setting.qualifier}
                                    options={getWeatherConditionQualifier(
                                        conditionOptions,
                                        setting.type
                                    )}
                                    placeholder={hasConditionSelected ? 'Select qualifier' : '—'}
                                    disabled={!hasConditionSelected}
                                    onChange={event => {
                                        onChange({ field: 'qualifier', value: event.target.value });
                                    }}
                                    error={
                                        showErrors &&
                                        errors[`rotation_rules.weather.${draftIndex}.qualifier`]
                                    }
                                />
                            ) : (
                                <React.Fragment>
                                    <Box mb={1}>
                                        <FormLabel component="legend" focused={false}>
                                            Qualifier
                                        </FormLabel>
                                    </Box>
                                    <TextField
                                        fullWidth
                                        disabled={true}
                                        variant="outlined"
                                        placeholder="equals to"
                                    />
                                </React.Fragment>
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            {weatherData.isWeatherTypeTemperature(setting.type) ? (
                                <React.Fragment>
                                    <Box mb={1}>
                                        <FormLabel
                                            component="legend"
                                            focused={false}
                                            required={hasConditionSelected}
                                        >
                                            Value
                                        </FormLabel>
                                    </Box>
                                    <TextFieldFormatter
                                        initialValue={setting.amount}
                                        onChange={v =>
                                            onChange({
                                                field: 'amount',
                                                value: v,
                                            })
                                        }
                                        shouldAllowChange={v => !/[a-zA-Z]/.test(v)}
                                        formatIn={v => numeral(v).value()}
                                        formatOut={v => numeral(v).value()}
                                        renderInput={({ value, onChange, onBlur }) => (
                                            <TextField
                                                fullWidth
                                                variant="outlined"
                                                error={
                                                    showErrors &&
                                                    errors[
                                                        `rotation_rules.weather.${draftIndex}.amount`
                                                    ]
                                                }
                                                value={value}
                                                onChange={onChange}
                                                onBlur={onBlur}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            {
                                                                weatherData.inputAdornmentMap[
                                                                    setting.type
                                                                ]
                                                            }
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                placeholder="Input value"
                                            />
                                        )}
                                    />
                                </React.Fragment>
                            ) : (
                                <SimpleSelect
                                    label="Value"
                                    required={hasConditionSelected}
                                    value={setting.amount === -1 ? '' : setting.amount}
                                    options={getWeatherConditionValue(
                                        conditionOptions,
                                        setting.type
                                    )}
                                    disabled={!hasConditionSelected}
                                    placeholder={hasConditionSelected ? 'Select value' : '—'}
                                    onChange={event => {
                                        onChange({ field: 'amount', value: event.target.value });
                                    }}
                                    error={
                                        showErrors &&
                                        errors[`rotation_rules.weather.${draftIndex}.amount`]
                                    }
                                />
                            )}
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <SimpleSelect
                                label="Time frame"
                                required={hasConditionSelected}
                                value={setting.timeframe}
                                options={getWeatherConditionTimeframe(
                                    conditionOptions,
                                    setting.type
                                )}
                                placeholder={hasConditionSelected ? 'Select time frame' : '—'}
                                disabled={!hasConditionSelected}
                                onChange={event => {
                                    onChange({ field: 'timeframe', value: event.target.value });
                                }}
                                error={
                                    showErrors &&
                                    errors[`rotation_rules.weather.${draftIndex}.timeframe`]
                                }
                            />
                        </Grid>
                        <Grid item xs={12} className={classes.creativeField}>
                            <CreativeSelect
                                setting={setting}
                                draftIndex={draftIndex}
                                creatives={creatives}
                                onChange={onChange}
                                showErrors={showErrors}
                                errors={errors}
                                isCrossPlatformCampaign={isCrossPlatformCampaign}
                                useAspectRatio={useAspectRatio}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </OrCardBody>
        </OrCard>
    );
};

/**
 * @param {Object} props
 * @param {Array<Object>} props.creatives - Campaign creatives for the selector
 * @param {Object} props.setting - Rule row state including markupId
 * @param {Function} props.onChange - Field update handler
 * @param {number} props.draftIndex - Index in rotation_rules.weather array for error keys
 * @param {boolean} props.showErrors - Whether to show validation errors
 * @param {Object} props.errors - Form validation errors
 * @param {boolean} props.isCrossPlatformCampaign - Shows platform chips on creative options
 * @param {boolean} props.useAspectRatio - Display aspect ratio instead of size on creatives
 */
const CreativeSelect = ({
    creatives,
    setting,
    onChange,
    draftIndex,
    showErrors,
    errors,
    isCrossPlatformCampaign,
    useAspectRatio,
}) => {
    const classes = useStyles();
    const hasCreativeSelected = Boolean(setting.markupId);

    return (
        <React.Fragment>
            <CustomContentSelect
                label="Creative"
                required={true}
                placeholder="Select a creative"
                value={setting.markupId}
                error={showErrors && errors[`rotation_rules.weather.${draftIndex}.markupId`]}
                onChange={event => {
                    const value = event.target.value;
                    const creative = creatives.find(c => c.creativeId === value);
                    onChange({ field: 'markupId', value: creative.disableSelection ? '' : value });
                }}
                options={_.map(creatives, creative => ({
                    label: `#${creative.creativeId} ${creative.name}`,
                    value: creative.creativeId,
                    content: (
                        <Grid
                            container
                            className={
                                creative.disableSelection ? classes.disabledSelectOption : ''
                            }
                        >
                            <Grid item xs={4} className={classes.previewImgColumn}>
                                <img
                                    className={classes.previewImgSelector}
                                    src={creative.previewImg}
                                    alt={creative.name}
                                />
                            </Grid>
                            <Grid item xs={7} className={classes.creativeInfoSelector}>
                                <Typography gutterBottom>
                                    {`#${creative.creativeId} ${creative.name}`}
                                </Typography>
                                {isCrossPlatformCampaign && (
                                    <Grid container direction="row" spacing={1}>
                                        {_.map(creative.platforms, platform => (
                                            <Grid item key={platform}>
                                                <Chip
                                                    size="small"
                                                    label={PLATFORM_MAPPING[platform].label}
                                                    variant="outlined"
                                                    color="primary"
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                )}
                                <div className="ef3-creativeRotationSelector_tags">
                                    <span className="ef3-creativeRotationSelector_tag">
                                        {useAspectRatio ? creative.aspect_ratio : creative.size}
                                    </span>
                                    <span className="ef3-creativeRotationSelector_tag">
                                        {creative.type}
                                    </span>
                                </div>
                            </Grid>
                        </Grid>
                    ),
                }))}
            />
            {!hasCreativeSelected && (
                <Box className={classes.creativeSelectHeightSpacer} aria-hidden="true" />
            )}
        </React.Fragment>
    );
};
